import { Capacitor } from '@capacitor/core'
import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { db } from '../../database/schema.js'
import { logger } from '../../utils/logger.js'
import type { NPSAlert, WildlifeCacheEntry } from './wildlife-types.js'

const log = logger.create('nps-alerts')

const DATA_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const ALERTS_URL = `${DATA_ORIGIN}/data/nps/alerts.json`

const ALERTS_CACHE_TTL_MS = 6 * 60 * 60 * 1000

const SOURCE_ID = 'nps-alerts'
const POINTS_LAYER = 'nps-alerts-points'

const MIN_ZOOM = 4
const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

const CATEGORY_ICONS: Record<string, { icon: string; color: string }> = {
  Danger: { icon: 'warning', color: '#dc2626' },
  Caution: { icon: 'warning', color: '#d97706' },
  'Park Closure': { icon: 'block', color: '#dc2626' },
  Information: { icon: 'info', color: '#2563eb' },
}

function registerAlertIcon(
  map: maplibregl.Map,
  imageId: string,
  iconName: string,
  color: string
): void {
  if (map.hasImage(imageId)) return

  const dpr = window.devicePixelRatio || 1
  const size = 28
  const canvas = document.createElement('canvas')
  canvas.width = size * dpr
  canvas.height = size * dpr
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  const cx = size / 2
  const cy = size / 2

  if (iconName === 'warning') {
    ctx.beginPath()
    ctx.moveTo(cx, 3)
    ctx.lineTo(size - 2, size - 3)
    ctx.lineTo(2, size - 3)
    ctx.closePath()
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 1.5
    ctx.stroke()

    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 14px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('!', cx, cy + 3)
  } else {
    const r = size / 2 - 2
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 1.5
    ctx.stroke()

    if (document.fonts.check('14px "Material Symbols Outlined"')) {
      ctx.font = '14px "Material Symbols Outlined"'
      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(iconName, cx, cy)
    }
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  map.addImage(imageId, imageData, { pixelRatio: dpr })
}

const ALL_CATEGORIES: NPSAlert['category'][] = ['Danger', 'Caution', 'Park Closure', 'Information']
const HAZARD_CATEGORIES: ReadonlySet<NPSAlert['category']> = new Set(['Danger', 'Caution'])

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

class NPSAlertsService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private alerts: NPSAlert[] = []
  private _activeCategories = new Set<NPSAlert['category']>(ALL_CATEGORIES)
  private loadingPromise: Promise<void> | null = null

  get enabled(): boolean {
    return this._enabled
  }

  get activeCategories(): ReadonlySet<NPSAlert['category']> {
    return this._activeCategories
  }

  setCategoryFilter(categories: NPSAlert['category'][]): void {
    this._activeCategories = new Set(categories.length > 0 ? categories : ALL_CATEGORIES)
    if (this._enabled) this.updateMapData()
  }

  register(map: maplibregl.Map): void {
    this.map = map
    for (const [category, conf] of Object.entries(CATEGORY_ICONS)) {
      registerAlertIcon(map, `nps-alert-${category}`, conf.icon, conf.color)
    }
  }

  unregister(): void {
    if (this._enabled) this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    this.addLayers()
    void this.loadAlerts()
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.removeLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  reAdd(): void {
    if (!this.map || !this._enabled) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.addLayers()
      this.updateMapData()
    }
  }

  async queryNearPoint(
    lat: number,
    lng: number,
    radiusKm = 80
  ): Promise<(NPSAlert & { distanceKm: number })[]> {
    await this.ensureAlertsLoaded()
    const results: (NPSAlert & { distanceKm: number })[] = []
    for (const a of this.alerts) {
      if (!HAZARD_CATEGORIES.has(a.category)) continue
      const dist = haversineKm(lat, lng, a.lat, a.lon)
      if (dist <= radiusKm) results.push({ ...a, distanceKm: dist })
    }
    results.sort((a, b) => a.distanceKm - b.distanceKm)
    return results
  }

  async queryFeaturesAtPoint(point: maplibregl.Point): Promise<NPSAlert[]> {
    if (!this.map || !this._enabled) return []

    const hits = this.map.getLayer(POINTS_LAYER)
      ? this.map.queryRenderedFeatures(point, { layers: [POINTS_LAYER] })
      : []

    const results: NPSAlert[] = []
    for (const hit of hits) {
      if (hit.properties) {
        const id = hit.properties.id as string
        const alert = this.alerts.find(a => a.id === id)
        if (alert) results.push(alert)
      }
    }
    return results
  }

  private addLayers(): void {
    if (!this.map) return

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: EMPTY_GEOJSON,
      })
    }

    if (!this.map.getLayer(POINTS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: POINTS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          minzoom: MIN_ZOOM,
          layout: {
            'icon-image': [
              'match',
              ['get', 'category'],
              'Danger',
              'nps-alert-Danger',
              'Caution',
              'nps-alert-Caution',
              'Park Closure',
              'nps-alert-Park Closure',
              'Information',
              'nps-alert-Information',
              'nps-alert-Information',
            ],
            'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 10, 0.8, 14, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
        },
        'wildlife-nps-alerts'
      )
    }

    this.map.on('mouseenter', POINTS_LAYER, () => {
      if (this.map) this.map.getCanvas().style.cursor = 'pointer'
    })
    this.map.on('mouseleave', POINTS_LAYER, () => {
      if (this.map) this.map.getCanvas().style.cursor = ''
    })
  }

  private removeLayers(): void {
    if (!this.map) return
    if (this.map.getLayer(POINTS_LAYER)) this.map.removeLayer(POINTS_LAYER)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  private async ensureAlertsLoaded(): Promise<void> {
    if (this.alerts.length > 0) return
    if (this.loadingPromise) return this.loadingPromise

    this.loadingPromise = this.doEnsureAlertsLoaded()
    try {
      await this.loadingPromise
    } finally {
      this.loadingPromise = null
    }
  }

  private async doEnsureAlertsLoaded(): Promise<void> {
    try {
      const cached = await db.wildlifeCache.get('nps-alerts-all')
      if (cached) {
        const age = Date.now() - new Date(cached.fetchedAt).getTime()
        if (age < ALERTS_CACHE_TTL_MS) {
          this.alerts = cached.data as NPSAlert[]
          return
        }
      }
    } catch {
      // IndexedDB not available
    }

    await this.fetchAlerts()
  }

  private async loadAlerts(): Promise<void> {
    await this.ensureAlertsLoaded()
    this.updateMapData()
  }

  private async fetchAlerts(): Promise<void> {
    try {
      const resp = await fetch(ALERTS_URL)
      if (!resp.ok) {
        log.warn(`NPS alerts fetch failed: ${resp.status}`)
        return
      }

      const data = await resp.json()
      this.alerts = (data.alerts as NPSAlert[]) ?? []
      log.info(`Loaded ${this.alerts.length} NPS alerts from backend`)
    } catch (err) {
      log.warn('NPS alerts fetch failed', err)
      return
    }

    try {
      const entry: WildlifeCacheEntry = {
        key: 'nps-alerts-all',
        dataType: 'nps-alerts',
        data: this.alerts,
        fetchedAt: new Date().toISOString(),
      }
      await db.wildlifeCache.put(entry)
    } catch {
      // IndexedDB write failed
    }
  }

  private updateMapData(): void {
    if (!this.map) return
    const source = this.map.getSource<maplibregl.GeoJSONSource>(SOURCE_ID)
    if (!source) return

    const filtered = this.alerts.filter(a => this._activeCategories.has(a.category))
    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: filtered.map(alert => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [alert.lon, alert.lat] },
        properties: {
          id: alert.id,
          title: alert.title,
          category: alert.category,
          parkName: alert.parkName,
          parkCode: alert.parkCode,
        },
      })),
    }
    source.setData(fc)
  }
}

export const npsAlertsService = new NPSAlertsService()
